import { LedgerAccountJson } from "@/api/accounting"

/**
 * Accounting systems whose credit memos are item-based: each GL line posts through a
 * product/service item resolved from the ledger account's `product_service` mapping.
 */
export function is_item_based_accounting_system(accounting_system: string): boolean {
  return accounting_system === "QuickBooks" || accounting_system === "NetSuite"
}

/**
 * The item (product/service) a credit-memo GL line resolves to: the item the line itself carries
 * (captured from the coded ledger row at build time — a GL account can map to several items, one
 * ledger row each), else the matching ledger account's `product_service` (accounts match by account
 * number or remote id, mirroring the publish paths).
 * Returns undefined when unmapped — the publish paths then fall back to using the raw GL account id
 * as the item name, which usually means the mapping still needs to be configured.
 */
export function credit_memo_line_product_service(
  line: { gl_account_id: string; product_service?: string | null },
  accounts: LedgerAccountJson[]
): string | undefined {
  const carried = line.product_service?.trim()
  if (carried) {
    return carried
  }
  const account = accounts.find((a) => a.account_number === line.gl_account_id || a.remote_id === line.gl_account_id)
  const mapped = account?.product_service?.trim()
  return mapped ? mapped : undefined
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function account(overrides: Partial<LedgerAccountJson>): LedgerAccountJson {
    return {
      id: "a",
      account_number: "48003",
      expense_account: "Contra Revenue : Promo Discounts",
      remote_id: "201",
      is_default_for_payments: false,
      hidden_from_deduction_matching: false,
      ...overrides,
    }
  }

  describe("credit_memo_line_product_service", () => {
    it("maps by account number or remote id", () => {
      const accounts = [account({ product_service: "Promo Discounts - NEW" })]
      expect(credit_memo_line_product_service({ gl_account_id: "48003" }, accounts)).toBe("Promo Discounts - NEW")
      expect(credit_memo_line_product_service({ gl_account_id: "201" }, accounts)).toBe("Promo Discounts - NEW")
    })

    it("prefers the item carried on the line over the account mapping", () => {
      // A GL account can map to several items (one ledger row each) — the line knows which one it
      // was coded to, so the account-number lookup must not override it.
      const accounts = [account({ product_service: "Delivery Chargebacks" })]
      expect(
        credit_memo_line_product_service({ gl_account_id: "48003", product_service: "Shortages (Wholesale)" }, accounts)
      ).toBe("Shortages (Wholesale)")
      expect(credit_memo_line_product_service({ gl_account_id: "48003", product_service: "  " }, accounts)).toBe(
        "Delivery Chargebacks"
      )
    })

    it("returns undefined for unmapped or unknown accounts", () => {
      expect(credit_memo_line_product_service({ gl_account_id: "48003" }, [account({ product_service: null })])).toBeUndefined()
      expect(credit_memo_line_product_service({ gl_account_id: "48003" }, [account({ product_service: "  " })])).toBeUndefined()
      expect(credit_memo_line_product_service({ gl_account_id: "99999" }, [account({ product_service: "X" })])).toBeUndefined()
    })
  })

  describe("is_item_based_accounting_system", () => {
    it("is true only for QuickBooks and NetSuite", () => {
      expect(is_item_based_accounting_system("QuickBooks")).toBe(true)
      expect(is_item_based_accounting_system("NetSuite")).toBe(true)
      expect(is_item_based_accounting_system("Sage Intacct")).toBe(false)
      expect(is_item_based_accounting_system("Xero")).toBe(false)
    })
  })
}
